/**
 * Subscription plans shown on landing, registration, and payment modal.
 * Prices in Malawi Kwacha (MWK).
 */

import { resolveSchoolVerificationStatus } from "../utils/schoolVerification";

export const PLAN_INCLUDES = [
    "Full system setup help",
    "Ongoing system support",
    "System usage guidance",
    "School website for parents & guardians",
];

export const SUBSCRIPTION_PLANS = [
    {
        id: "starter",
        name: "Starter",
        studentRange: "Fewer than 150 students",
        maxStudents: 149,
        minStudents: 0,
        priceMwk: 40_000,
        billingLabel: "per month",
        highlight: false,
    },
    {
        id: "standard",
        name: "Standard",
        studentRange: "150 – 250 students",
        maxStudents: 250,
        minStudents: 150,
        priceMwk: 60_000,
        billingLabel: "per month",
        highlight: true,
    },
    {
        id: "professional",
        name: "Professional",
        studentRange: "250 – 500 students",
        maxStudents: 500,
        minStudents: 251,
        priceMwk: 80_000,
        billingLabel: "per month",
        highlight: false,
    },
    {
        id: "enterprise",
        name: "Enterprise",
        studentRange: "500 – 700 students",
        maxStudents: 700,
        minStudents: 501,
        priceMwk: 100_000,
        billingLabel: "per month",
        highlight: false,
    },
    {
        id: "unlimited",
        name: "Unlimited",
        studentRange: "Unlimited students",
        maxStudents: null,
        minStudents: 701,
        priceMwk: 150_000,
        billingLabel: "per month",
        highlight: false,
    },
];

export const TRIAL_DAYS = 30;

/** One-time trial extension (same length as initial trial). */
export const TRIAL_EXTENSION_DAYS = TRIAL_DAYS;

/** TEMPORARY: set to 10 to test trial expiry in ~10 minutes. Must be 0 for production. */
export const TRIAL_TEST_MINUTES = 0;

export function getTrialPeriodLabel() {
    if (TRIAL_TEST_MINUTES > 0) {
        return `${TRIAL_TEST_MINUTES}-minute`;
    }
    if (TRIAL_DAYS === 30) return "1-month";
    return `${TRIAL_DAYS}-day`;
}

/** Seconds left on trial (0 = ended, null = unknown). Uses trialEndsAt when set (live clock). */
export function getTrialSecondsRemaining(user) {
    if (!user?.trialEndsAt) return null;
    return Math.max(0, Math.ceil((new Date(user.trialEndsAt).getTime() - Date.now()) / 1000));
}

/** Minutes left on trial (0 = ended, null = unknown). */
export function getTrialMinutesRemaining(user) {
    const secs = getTrialSecondsRemaining(user);
    if (secs != null) return Math.ceil(secs / 60);
    if (user?.subscriptionMinutesRemaining != null) {
        return Math.max(0, Number(user.subscriptionMinutesRemaining));
    }
    return null;
}

/** True when the school’s trial has ended (even if DB status is still "trialing"). */
export function isTrialExpired(user) {
    if (!user) return false;
    if (user.subscriptionStatus === "expired") return true;
    if (user.subscriptionStatus !== "trialing") return false;
    if (user.subscriptionActive === false) return true;
    const mins = getTrialMinutesRemaining(user);
    if (mins != null) return mins <= 0;
    const end = user.trialEndsAt || user.subscriptionEndsAt;
    if (end) return new Date(end).getTime() <= Date.now();
    const days = user.subscriptionDaysRemaining;
    if (days != null && days <= 0 && TRIAL_TEST_MINUTES <= 0) return true;
    return false;
}

export function getTrialRemainingLabel(user) {
    if (isTrialExpired(user)) return null;

    const mins = getTrialMinutesRemaining(user);

    if (TRIAL_TEST_MINUTES > 0 && mins != null) {
        const secs = getTrialSecondsRemaining(user);
        if (secs != null && secs <= (TRIAL_TEST_MINUTES + 2) * 60) {
            const m = Math.floor(secs / 60);
            const s = secs % 60;
            return `${m}:${String(s).padStart(2, "0")} left on your free trial`;
        }
        if (mins <= TRIAL_TEST_MINUTES + 15) {
            return `${mins} minute${mins === 1 ? "" : "s"} left on your free trial`;
        }
        const days = Math.max(1, Math.ceil(mins / (24 * 60)));
        return `${days} day${days === 1 ? "" : "s"} left on your free trial`;
    }

    if (mins != null && mins <= 0) return null;

    const days = user?.subscriptionDaysRemaining;
    if (days == null) return `your ${getTrialPeriodLabel()} free trial`;
    if (days <= 0) return null;
    return `${days} day${days === 1 ? "" : "s"} left on your free trial`;
}

/** True when this account was created under the minute-based test trial window. */
export function isShortTestTrial(user) {
    if (!TRIAL_TEST_MINUTES || !user?.trialEndsAt) return false;
    const end = new Date(user.trialEndsAt).getTime();
    const startMs = user.subscriptionStartedAt
        ? new Date(user.subscriptionStartedAt).getTime()
        : end - TRIAL_TEST_MINUTES * 60 * 1000;
    const durationMs = end - startMs;
    return durationMs > 0 && durationMs <= (TRIAL_TEST_MINUTES + 15) * 60 * 1000;
}

export function formatPriceMwk(amount) {
    return `MWK ${Number(amount).toLocaleString("en-MW")}`;
}

export function getPlanById(planId) {
    const raw = String(planId || "starter").trim().toLowerCase();
    const id = raw === "lifetime" ? "unlimited" : raw;
    return SUBSCRIPTION_PLANS.find((p) => p.id === id) || SUBSCRIPTION_PLANS[0];
}

/** True when the school cannot add another student on the current plan. */
export function isAtStudentLimit(user, studentCount = user?.schoolStudentCount) {
    const limit = user?.studentLimit;
    if (limit == null) return false;
    return Number(studentCount ?? 0) >= limit;
}

/** Suggest a plan id from current enrolment count. */
export function getRecommendedPlanId(studentCount) {
    const n = Number(studentCount) || 0;
    if (n >= 701) return "unlimited";
    if (n >= 501) return "enterprise";
    if (n >= 251) return "professional";
    if (n >= 150) return "standard";
    return "starter";
}

/** Read subscription access from logged-in admin or school page payload. */
export function isSubscriptionActive(userOrPage) {
    if (!userOrPage) return true;
    if (resolveSchoolVerificationStatus(userOrPage) === "pending") {
        return false;
    }
    if (userOrPage.subscriptionStatus === "pending_verification") return false;
    if (userOrPage.subscriptionStatus === "lifetime" || userOrPage.subscriptionStatus === "active") {
        return true;
    }
    if (userOrPage.subscriptionStatus === "trialing") {
        if (isTrialExpired(userOrPage)) return false;
        const end = userOrPage.trialEndsAt || userOrPage.subscriptionEndsAt;
        if (!end) return userOrPage.subscriptionActive !== false;
        return new Date(end).getTime() > Date.now();
    }
    if (userOrPage.subscriptionStatus === "expired") return false;
    if (userOrPage.subscriptionActive === false) return false;
    if (userOrPage.subscriptionActive === true) return true;
    return true;
}

export function subscriptionBlockedMessage(userOrPage) {
    const planName = userOrPage?.subscriptionPlanName || getPlanById(userOrPage?.subscriptionPlan).name;
    const price = userOrPage?.planPriceLabel || formatPriceMwk(getPlanById(userOrPage?.subscriptionPlan).priceMwk);
    if (userOrPage?.subscriptionReason === "trial_expired" || userOrPage?.subscriptionStatus === "expired") {
        const wasTrial = userOrPage?.subscriptionReason === "trial_expired";
        if (wasTrial) {
            return `Your ${getTrialPeriodLabel()} free trial on the ${planName} plan has ended. Please pay ${price} to continue.`;
        }
        return `Your ${planName} subscription has ended. Please pay ${price} to restore access.`;
    }
    return `Please renew your ${planName} plan (${price}) to continue using the system.`;
}
